import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'farma-g5-cart';

/** Quantities per kit SKU. Prices and stock always come from the API, never from the cart. */
export type CartQuantities = Readonly<Record<string, number>>;

interface CartState {
  readonly quantities: CartQuantities;
  readonly itemCount: number;
  readonly setQuantity: (sku: string, quantity: number) => void;
  readonly add: (sku: string, maxQuantity: number) => void;
  readonly remove: (sku: string) => void;
  readonly clear: () => void;
}

const CartContext = createContext<CartState | undefined>(undefined);

function readStoredCart(): CartQuantities {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw === null ? {} : JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] => Number.isInteger(entry[1]) && Number(entry[1]) > 0,
      ),
    );
  } catch {
    return {};
  }
}

function storeCart(quantities: CartQuantities): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(quantities));
  } catch {
    // Private mode or blocked storage: the cart still works for this page view.
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [quantities, setQuantities] = useState<CartQuantities>(readStoredCart);
  useEffect(() => {
    storeCart(quantities);
  }, [quantities]);

  const setQuantity = useCallback((sku: string, quantity: number) => {
    setQuantities((current) => {
      const next = { ...current };
      if (quantity <= 0) delete next[sku];
      else next[sku] = quantity;
      return next;
    });
  }, []);
  const add = useCallback((sku: string, maxQuantity: number) => {
    setQuantities((current) => ({
      ...current,
      [sku]: Math.min(maxQuantity, (current[sku] ?? 0) + 1),
    }));
  }, []);
  const remove = useCallback(
    (sku: string) => {
      setQuantity(sku, 0);
    },
    [setQuantity],
  );
  const clear = useCallback(() => {
    setQuantities({});
  }, []);

  const value = useMemo(
    () => ({
      quantities,
      itemCount: Object.values(quantities).reduce((sum, quantity) => sum + quantity, 0),
      setQuantity,
      add,
      remove,
      clear,
    }),
    [quantities, setQuantity, add, remove, clear],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const cart = useContext(CartContext);
  if (cart === undefined) throw new Error('useCart must be used inside CartProvider');
  return cart;
}
